import React from "react";
import { useTranslation } from "react-i18next";
import { User, AlertCircle, Calendar, Clock, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { getStatusBadge, getPriorityBadge, formatDate } from "./overrideApprovalHelpers";

const getEmployeeName = (employees, employeeId, t) => {
  const emp = employees.find((e) => e.id === employeeId);
  return emp ? `${emp.last_name} ${emp.first_name}` : t("planning.overridesPanel.unknownEmployee");
};

const getScheduleName = (schedules, scheduleId, t) => {
  const schedule = schedules.find((s) => s.id === scheduleId);
  return schedule?.name || t("planning.overridesPanel.unknownSchedule");
};

const ApprovalOverrideCard = ({ override, employees, schedules, handleOpenApprovalDialog }) => {
  const { t, i18n } = useTranslation();

  return (
    <Card className={override.status === "pending" ? "border-yellow-900/40" : ""}>
    <CardContent className="pt-6">
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <User className="h-4 w-4 text-gray-400" />
              <span className="font-medium">{getEmployeeName(employees, override.employee_id, t)}</span>
              {getStatusBadge(override.status, t)}
              {getPriorityBadge(override.priority, t)}
              {override.is_exceptional && (
                <Badge variant="outline" className="text-amber-600">{t("planning.overridesPanel.exceptionalBadge")}</Badge>
              )}
            </div>
          </div>
          {override.status === "pending" && (
            <div className="flex items-center gap-2 ml-4">
              <Button
                size="sm"
                variant="outline"
                className="text-green-400 hover:text-green-300 hover:border-green-500"
                onClick={() => handleOpenApprovalDialog(override, "approve")}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {t("planning.approvalsPanel.approve")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-400 hover:text-red-300 hover:border-red-500"
                onClick={() => handleOpenApprovalDialog(override, "reject")}
              >
                <XCircle className="h-4 w-4 mr-1" />
                {t("planning.approvalsPanel.reject")}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">{t("planning.approvalsPanel.requestedDate")}</span>
            <p className="font-medium mt-1">{formatDate(override.date, i18n.language || "fr")}</p>
          </div>
          <div>
            <span className="text-muted-foreground">{t("planning.overridesPanel.requestedScheduleLabel")}</span>
            <p className="font-medium mt-1">{getScheduleName(schedules, override.override_schedule_id, t)}</p>
          </div>
        </div>

        <div>
          <span className="text-sm font-medium text-muted-foreground">{t("planning.overridesPanel.reasonLabel")}</span>
          <p className="mt-1 text-foreground bg-muted/50 p-3 rounded">{override.reason}</p>
        </div>

        {override.status === "rejected" && override.rejection_reason && (
          <div className="flex items-start gap-2 p-3 bg-red-950/20 border border-red-900/30 rounded">
            <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{t("planning.overridesPanel.rejectionReason")}</p>
              <p className="text-sm text-muted-foreground mt-1">{override.rejection_reason}</p>
            </div>
          </div>
        )}

        {override.approved_at && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Clock className="h-3 w-3" />
            <span>
              {t("planning.overridesPanel.processedOn", {
                status: override.status === "approved"
                  ? t("planning.overridesPanel.statuses.approved")
                  : t("planning.overridesPanel.statuses.rejected"),
                date: new Date(override.approved_at).toLocaleString(i18n.language || "fr"),
              })}
            </span>
          </div>
        )}

        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Calendar className="h-3 w-3" />
          <span>
            {t("planning.approvalsPanel.createdOn", {
              date: new Date(override.created_at).toLocaleString(i18n.language || "fr"),
            })}
          </span>
        </div>
      </div>
    </CardContent>
  </Card>
  );
};

export default ApprovalOverrideCard;
